import type { PriceListRequest } from '@erp/shared';
import { Check, Eye, UserCheck, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import {
  Badge, Card, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Skeleton,
} from '../../components/shadcn/index.js';
import { Button, confirmDialog, DataTable, type DataTableColumn, EmptyState } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { formatDate } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';
import { approvePriceListRequest, listPriceListRequests, rejectPriceListRequest } from '../../lib/pricing.js';

/**
 * Approve/reject the "customer type" (price list) a shopper self-selected at
 * signup. Approving copies the requested list onto the customer's applied price
 * list (so their storefront prices switch to that group); rejecting clears the
 * request and leaves them on normal/base pricing.
 */
export function CustomerRequestsPage(): JSX.Element {
  const { t, i18n } = useTranslation('pricing');
  const { t: tc } = useTranslation();
  const canWriteParties = usePermission('parties.write');
  const canWriteCustomers = usePermission('customers.write');
  const canWrite = canWriteParties || canWriteCustomers;
  const [rows, setRows] = useState<PriceListRequest[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  /** The request being reviewed in the modal, if any. */
  const [viewing, setViewing] = useState<PriceListRequest | null>(null);

  const reload = async (): Promise<void> => { setRows(await listPriceListRequests()); };
  useEffect(() => { void reload(); }, []);

  if (!rows) {
    return <Card className="p-6"><Skeleton className="h-6 w-48" /><Skeleton className="mt-4 h-32 w-full" /></Card>;
  }

  const approve = async (r: PriceListRequest): Promise<void> => {
    if (!(await confirmDialog({ message: t('requests.confirmApprove', { name: r.displayName, type: r.requestedPriceListName }) }))) return;
    setBusyId(r.id);
    try { await approvePriceListRequest(r.id); toast.success(t('requests.approved')); await reload(); }
    catch { toast.error(tc('errors:server')); } finally { setBusyId(null); }
  };
  const reject = async (r: PriceListRequest): Promise<void> => {
    if (!(await confirmDialog({ message: t('requests.confirmReject', { name: r.displayName }), tone: 'danger' }))) return;
    setBusyId(r.id);
    try { await rejectPriceListRequest(r.id); toast.success(t('requests.rejected')); await reload(); }
    catch { toast.error(tc('errors:server')); } finally { setBusyId(null); }
  };

  const columns: DataTableColumn<PriceListRequest>[] = [
    {
      id: 'name', header: t('requests.columns.customer'),
      sortValue: (r) => r.displayName, searchValue: (r) => `${r.displayName} ${r.phone ?? ''} ${r.code}`, cellClassName: 'font-medium',
      cell: (r) => (<div><div>{r.displayName}</div><div className="font-mono text-[11px] text-fg-subtle">{r.code}</div></div>),
    },
    { id: 'phone', header: t('requests.columns.phone'), sortValue: (r) => r.phone ?? '', cell: (r) => r.phone ?? <span className="text-fg-muted">—</span> },
    { id: 'type', header: t('requests.columns.requested'), sortValue: (r) => r.requestedPriceListName, cell: (r) => <Badge variant="secondary">{r.requestedPriceListName}</Badge> },
    { id: 'date', header: t('requests.columns.requestedAt'), sortValue: (r) => r.createdAt, cell: (r) => formatDate(r.createdAt, { locale: i18n.language }) },
    // Trading history in the row itself — the single most useful column when the
    // question is "is this a real shop?", and it saves opening the modal at all for
    // the obvious cases.
    {
      id: 'orders', header: t('requests.columns.orders', { defaultValue: 'Orders' }),
      sortValue: (r) => r.orderCount,
      cell: (r) => (r.orderCount === 0
        ? <span className="text-fg-muted">—</span>
        : <span className="tabular-nums">{r.orderCount}</span>),
    },
    {
      id: 'view', header: '', align: 'end' as const, headClassName: 'w-12',
      cell: (r: PriceListRequest) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setViewing(r)}
          aria-label={t('requests.view', { defaultValue: 'View details' })}
          title={t('requests.view', { defaultValue: 'View details' })}
        >
          <Eye className="h-4 w-4" aria-hidden />
        </Button>
      ),
    },
    ...(canWrite ? [{
      id: 'actions', header: '', align: 'end' as const, headClassName: 'w-56',
      cell: (r: PriceListRequest) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" loading={busyId === r.id} onClick={() => void approve(r)}>
            <Check className="h-4 w-4 text-success" aria-hidden />{t('requests.approve')}
          </Button>
          <Button variant="ghost" size="sm" disabled={busyId === r.id} onClick={() => void reject(r)} title={t('requests.reject')}>
            <X className="h-4 w-4 text-danger" aria-hidden />{t('requests.reject')}
          </Button>
        </div>
      ),
    }] : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{t('requests.title')}</h1>
          <p className="mt-1 max-w-2xl text-sm text-fg-muted">{t('requests.description')}</p>
        </div>
        {rows.length > 0 && <Badge variant="warning">{t('requests.pending', { count: rows.length })}</Badge>}
      </header>

      {rows.length === 0 ? (
        <EmptyState icon={<UserCheck className="h-8 w-8" aria-hidden />} title={t('requests.empty')} description={t('requests.emptyDescription')} />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable columns={columns} data={rows} rowKey={(r) => r.id} searchPlaceholder={t('requests.columns.customer')} />
        </Card>
      )}

      {/* Review before approving. Approving puts this shopper on a wholesale tier, and
          the row alone (a name and a phone number) is not enough to tell a real ماركيت
          from someone who ticked the box for the cheaper prices — so the modal leads
          with what they have actually bought. */}
      <Dialog open={Boolean(viewing)} onOpenChange={(o) => { if (!o) setViewing(null); }}>
        <DialogContent className="max-w-lg">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle>{viewing.displayName}</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col gap-4 py-1">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-fg-muted">{t('requests.columns.requested')}:</span>
                  <Badge variant="warning">{viewing.requestedPriceListName}</Badge>
                  <span className="text-fg-muted">←</span>
                  <Badge variant="secondary">
                    {viewing.currentPriceListName ?? t('requests.normalCustomer', { defaultValue: 'Normal customer' })}
                  </Badge>
                </div>

                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                  <Row label={t('requests.columns.customer')} value={<span className="font-mono text-xs">{viewing.code}</span>} />
                  <Row label={t('requests.columns.phone')} value={viewing.phone} ltr />
                  <Row label={t('requests.email', { defaultValue: 'Email' })} value={viewing.email} ltr />
                  <Row label={t('requests.address', { defaultValue: 'Address' })} value={viewing.address} />
                  <Row label={t('requests.columns.requestedAt')} value={formatDate(viewing.createdAt, { locale: i18n.language })} />
                  <Row
                    label={t('requests.portalLogin', { defaultValue: 'Store account' })}
                    value={viewing.hasPortalLogin
                      ? t('requests.yes', { defaultValue: 'Yes' })
                      : t('requests.guestOnly', { defaultValue: 'Guest checkout only' })}
                  />
                </dl>

                <div className="rounded-lg border border-border p-3">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">
                    {t('requests.history', { defaultValue: 'Trading history' })}
                  </div>
                  {viewing.orderCount === 0 ? (
                    <p className="text-sm text-warning">
                      {t('requests.noOrders', { defaultValue: 'Has never ordered — nothing to judge a wholesale tier on yet.' })}
                    </p>
                  ) : (
                    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                      <Row label={t('requests.columns.orders', { defaultValue: 'Orders' })} value={String(viewing.orderCount)} />
                      <Row
                        label={t('requests.lastOrder', { defaultValue: 'Last order' })}
                        value={viewing.lastOrderAt ? formatDate(viewing.lastOrderAt, { locale: i18n.language }) : '—'}
                      />
                      <Row
                        label={t('requests.lifetimeValue', { defaultValue: 'Total bought' })}
                        // Per currency, never summed across them.
                        value={viewing.orderTotals.map((v) => formatMoney(v.amount, { currency: v.currencyCode, locale: i18n.language })).join(' · ')}
                      />
                    </dl>
                  )}
                  <Link
                    to={`/sales/parties/${viewing.id}`}
                    className="mt-3 inline-block text-xs font-medium text-brand hover:underline"
                    onClick={() => setViewing(null)}
                  >
                    {t('requests.openCustomer', { defaultValue: 'Open the full customer record →' })}
                  </Link>
                </div>
              </div>
              {canWrite && (
                <DialogFooter>
                  <Button
                    variant="secondary"
                    disabled={busyId === viewing.id}
                    onClick={() => { const r = viewing; setViewing(null); void reject(r); }}
                  >
                    <X className="h-4 w-4 text-danger" aria-hidden />{t('requests.reject')}
                  </Button>
                  <Button
                    loading={busyId === viewing.id}
                    onClick={() => { const r = viewing; setViewing(null); void approve(r); }}
                  >
                    <Check className="h-4 w-4" aria-hidden />{t('requests.approve')}
                  </Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** One label/value pair in the review modal; empty values read as an em dash, not blank. */
function Row({ label, value, ltr }: { label: string; value: React.ReactNode; ltr?: boolean }): JSX.Element {
  return (
    <>
      <dt className="text-fg-muted">{label}</dt>
      <dd className={ltr ? 'font-mono text-xs' : undefined} {...(ltr ? { dir: 'ltr' } : {})}>
        {value || <span className="text-fg-muted">—</span>}
      </dd>
    </>
  );
}
